import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '@proptr/api-client/react';
import { createClient } from '@proptr/api-client';
import { ThemeProvider } from '@proptr/ui';
// Self-hosted before anything else: the creative templates must never
// depend on a third-party CDN for the faces they render with.
import './fonts.css';
import './index.css';
import { App } from './App.js';
import { isTestMode } from './lib/testMode.js';

// Boot order matters here.
//
// While Testing mode is on, we first pull this brokerage's own demo dataset
// (their agency's listings + team, imported at signup) using the UNPATCHED
// fetch — patch first and the interceptor would swallow its own request.
// Only then do we dynamically import the interceptor, because that module
// evaluates testFixtures.ts, which snapshots the dataset.
//
// Toggling the switch already reloads the app, so this all happens before
// first paint: no flip from static data to agency data mid-session. If the
// fetch fails, or the enterprise never picked an agency, the static fixture
// stays in place — which is exactly the previous behaviour.
async function bootTestMode() {
  if (isTestMode()) {
    const { loadDemoSource } = await import('./lib/demoSource.js');
    await loadDemoSource();
  }
  const { installTestModeFetch } = await import('./lib/testModeFetch.js');
  installTestModeFetch();
}

const IMP_TOKEN_KEY = 'proptr_imp_token';

// When admin opens this app via "Login as", the URL carries `?_imp=<jwt>`.
// Move it into sessionStorage (tab-scoped — no leak to other tabs, no leak
// to the cookie jar) and strip it from the URL before React mounts so the
// router never sees the param. We deliberately never put the token in
// localStorage; this means closing the tab cleanly ends the impersonation.
(function captureImpersonationToken() {
  try {
    const url = new URL(window.location.href);
    const imp = url.searchParams.get('_imp');
    if (imp) {
      window.sessionStorage.setItem(IMP_TOKEN_KEY, imp);
      url.searchParams.delete('_imp');
      window.history.replaceState({}, '', url.toString());
    }
  } catch {
    // sessionStorage unavailable (private mode etc.) — fall through; the
    // app will just not have an impersonation session.
  }
})();

export const client = createClient({
  baseUrl: import.meta.env.VITE_API_URL ?? '',
  // Read on every request — sessionStorage is the source of truth for the
  // current tab's impersonation state. Returning null falls back to the
  // normal cookie auth.
  getBearerToken: () => {
    try {
      return window.sessionStorage.getItem(IMP_TOKEN_KEY);
    } catch {
      return null;
    }
  },
});

// Exposed so the impersonation banner's "Exit" handler can clear it.
export function clearImpersonationToken(): void {
  try {
    window.sessionStorage.removeItem(IMP_TOKEN_KEY);
  } catch {
    // ignore
  }
}

// Mounting inside a promise means Vite's HMR can re-run this module and call
// createRoot twice on the same container. Cache the root on the container so
// a re-run re-renders instead of re-creating.
const container = document.getElementById('root')! as HTMLElement & {
  __root?: ReactDOM.Root;
};

void bootTestMode().then(() => {
  container.__root ??= ReactDOM.createRoot(container);
  container.__root.render(
    <React.StrictMode>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider client={client}>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </React.StrictMode>,
  );
});
